/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleComponentsEditDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import EnterSelectTextField from "../text-fields/EnterSelectTextField";
import {
  HandleApiActions,
  adminSettingsAPI,
  labCar,
} from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import CustomDialogBoxV2 from "../customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";
import MultiselectConnectorV2 from "../customComponentsV2/common/connectors/MultiselectConnectorV2";
import CustomCheckBoxV2 from "../customComponentsV2/common/checkBox/CustomCheckBoxV2";
import AttributesForm from "../../pages/labcar/Attributes/AttributesForm";
import CustomTabsV2 from "../customComponentsV2/common/tabs/CustomTabsV2";

function VehicleComponentsEditDialog({
  openDialog,
  setOpenDialog,
  selectedVCItem,
  variantOptionList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  supplierOptionsList,
  vcOptionsList = [],
  selectedVehicleSystemId,
  selectedVehicleSystemDetails,
  getVehicleComponents,
}) {
  const dispatch = useDispatch();
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
  );

  const [selectedVehicleComponent, setSelectedVehicleComponent] = useState("");
  const [componentNo, setComponentNo] = useState("");
  const [description, setDescription] = useState("");
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [reason, setReason] = useState("");
  const [addInSettings, setAddInSettings] = useState(false);
  const [addInSettingsReason, setAddInSettingsReason] = useState("");
  const [validateShowAddInSettings, setValidateShowAddInSettings] =
    useState(false);
  const [vsVariantOptionList, setVsVariantOptionList] = useState([]);
  const [initialFormState, setInitialFormState] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [hasPendingVCAddRequest, setHasPendingVCAddRequest] = useState(false);
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [vcEditTabValue, setVcEditTabValue] = useState(0);

  const normalizeSupplierOptions = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((opt) => {
        if (!opt) return null;
        if (typeof opt === "string") return { value: opt, label: opt };
        const value = opt.value ?? opt.id ?? opt.name ?? opt.label;
        const label = opt.label ?? opt.name ?? opt.value ?? opt.id;
        if (value == null || label == null) return null;
        return { value, label };
      })
      .filter(Boolean);
  };

  const suppliersFromProps = useMemo(
    () => normalizeSupplierOptions(supplierOptionsList),
    [supplierOptionsList],
  );
  const suppliersFromStore = useMemo(() => {
    const list = adminSettingsDetails?.adminSettings?.data?.ecu_suppliers;
    return normalizeSupplierOptions(list);
  }, [adminSettingsDetails]);
  const resolvedSupplierOptionsList =
    suppliersFromProps.length > 0 ? suppliersFromProps : suppliersFromStore;

  // Fetch admin settings when edit dialog opens if suppliers are not available yet.
  useEffect(() => {
    if (!openDialog) return;
    if (resolvedSupplierOptionsList.length > 0) return;

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_admin_settings,
        show_toast: false,
      }),
    );
    setVcEditTabValue(0);
  }, [openDialog, resolvedSupplierOptionsList.length, dispatch]);

  useEffect(() => {
    setAddInSettings(false);
    setAddInSettingsReason("");
  }, [selectedVehicleComponent]);

  useEffect(() => {
    if (!openDialog) return;
    const vcName = String(selectedVehicleComponent || "").trim();
    if (!vcName) {
      setHasPendingVCAddRequest(false);
      return;
    }

    let isActive = true;
    const checkVCAddRequest = async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_vc_add_requests,
          show_toast: false,
          params: {
            status: 100,
            vc_name: vcName,
          },
        }),
      );
      if (!isActive) return;
      const hasPending = !resp?.error && Number(resp?.total_count || 0) > 0;
      setHasPendingVCAddRequest(hasPending);
      if (hasPending) {
        setAddInSettings(false);
        setAddInSettingsReason("");
      }
    };
    checkVCAddRequest();

    return () => {
      isActive = false;
    };
  }, [openDialog, selectedVehicleComponent, dispatch]);

  useEffect(() => {
    const filteredVcOptionsList =
      vcOptionsList?.map((vcOL) => vcOL?.label?.toUpperCase()) ?? [];
    const showAddInSettings = selectedVehicleComponent
      ? filteredVcOptionsList.includes(selectedVehicleComponent?.toUpperCase())
      : false;
    setValidateShowAddInSettings(showAddInSettings || false);
  }, [vcOptionsList, selectedVehicleComponent]);

  useEffect(() => {
    let filteredVariantOptionList =
      variantOptionList?.filter((option) =>
        selectedVehicleSystemDetails?.variant_ids?.includes(
          Number(option?.value),
        ),
      ) || [];
    if (selectedSingleVariantDetails?.variant_id != null) {
      const activeId = selectedSingleVariantDetails.variant_id;
      const hasActive = filteredVariantOptionList.some(
        (option) => String(option.value) === String(activeId),
      );
      if (!hasActive) {
        const found = variantOptionList?.find(
          (option) => String(option.value) === String(activeId),
        );
        if (found)
          filteredVariantOptionList = [found, ...filteredVariantOptionList];
      }
    }
    setVsVariantOptionList(filteredVariantOptionList);
  }, [
    variantOptionList,
    selectedVehicleSystemDetails,
    selectedSingleVariantDetails,
  ]);

  const getVCFieldValue = (item, keys = [], fallback = "") => {
    for (const key of keys) {
      const value = item?.[key];
      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return value;
      }
    }
    const vcDetails = item?.vehicle_component_details || item?.vc_details;
    for (const key of keys) {
      const value = vcDetails?.[key];
      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return value;
      }
    }
    return fallback;
  };

  const setFormFromItem = (item) => {
    const initialComponentNo = getVCFieldValue(
      item,
      ["component_number", "vc_number"],
      "",
    );
    const initialName = getVCFieldValue(
      item,
      ["name", "component_name", "vehicle_component_name", "vc_name"],
      "",
    );
    const initialDescription = getVCFieldValue(item, ["description"], "");
    const initialSuppliers =
      item?.suppliers || item?.vehicle_component_details?.suppliers || [];
    const detailsVariantIds = item?.vehicle_component_details?.variant_ids;
    const initialVariants =
      item?.variant_ids?.length > 0
        ? item?.variant_ids
        : detailsVariantIds?.length > 0
          ? detailsVariantIds
          : selectedSingleVariantDetails?.variant_id != null
            ? [selectedSingleVariantDetails?.variant_id]
            : [];

    setComponentNo(initialComponentNo);
    setSelectedVehicleComponent(initialName);
    setDescription(initialDescription);
    setSelectedSuppliers(initialSuppliers);
    setSelectedVariants(initialVariants);
    setReason("");
    setAddInSettings(false);
    setAddInSettingsReason("");
    setInitialFormState({
      componentNo: initialComponentNo,
      selectedVehicleComponent: initialName,
      description: initialDescription,
      selectedSuppliers: initialSuppliers,
      selectedVariants: initialVariants,
    });
  };

  useEffect(() => {
    if (!openDialog || !selectedVCItem) return;
    setAttributeFormData({ added: [], edited: [], deleted: [] });
    setVcEditTabValue(0);
  }, [openDialog, selectedVCItem]);

  useEffect(() => {
    if (!openDialog || !selectedVCItem) return;
    const fetchAndSetVCDetails = async () => {
      const projectId = selectedProjectDetails?.project_id;
      const variantId = selectedSingleVariantDetails?.variant_id;
      const vsId = selectedVehicleSystemId;
      const vcId = selectedVCItem?.id || selectedVCItem?.vc_id;

      // Immediate fallback from current item so dialog is not empty.
      setFormFromItem(selectedVCItem);

      if (!projectId || !variantId || !vsId || !vcId) return;

      setIsLoadingDetails(true);
      const resp = await dispatch(
        HandleApiActions({
          ...labCar.get_vehicle_components_all,
          show_toast: false,
          params: {
            project_id: Number(projectId),
            variant_id: Number(variantId),
            vs_id: Number(vsId),
            vc_id: Number(vcId),
          },
        }),
      );
      setIsLoadingDetails(false);

      if (resp?.error) return;
      const matchedDetails = (resp?.data || []).find(
        (vc) =>
          Number(vc?.id) === Number(vcId) || Number(vc?.vc_id) === Number(vcId),
      );
      if (matchedDetails) {
        setFormFromItem(matchedDetails);
      }
    };

    fetchAndSetVCDetails();
  }, [
    openDialog,
    selectedVCItem,
    selectedSingleVariantDetails,
    selectedProjectDetails,
    selectedVehicleSystemId,
    dispatch,
  ]);

  const hasFormChanged = () => {
    if (!initialFormState) return false;
    const sortAsStrings = (arr) =>
      (Array.isArray(arr) ? arr : []).map(String).sort();
    const suppliersChanged =
      JSON.stringify(sortAsStrings(selectedSuppliers)) !==
      JSON.stringify(sortAsStrings(initialFormState.selectedSuppliers));
    const variantsChanged =
      JSON.stringify(sortAsStrings(selectedVariants)) !==
      JSON.stringify(sortAsStrings(initialFormState.selectedVariants));

    return (
      componentNo?.trim() !== initialFormState.componentNo?.trim() ||
      selectedVehicleComponent !== initialFormState.selectedVehicleComponent ||
      description?.trim() !== initialFormState.description?.trim() ||
      suppliersChanged ||
      variantsChanged
    );
  };

  const isDialogDetailsValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      componentNo?.trim() !== "" &&
      regexMin3.test(componentNo.trim()) &&
      selectedVehicleComponent?.trim()?.length > 0 &&
      description?.trim()?.length >= 3 &&
      selectedSuppliers?.length > 0 &&
      (addInSettings ? addInSettingsReason?.trim()?.length >= 3 : true)
      // &&
      // hasFormChanged()
    );
  };

  const isDialogFieldsValid = () =>
    isDialogDetailsValid() && reason?.trim()?.length >= 3;

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
    setReason("");
    setVcEditTabValue(0);
  };

  const handleVCAttributeAdd = useCallback((data = {}) => {
    const added = Array.isArray(data?.added) ? data.added : [];
    const edited = Array.isArray(data?.edited) ? data.edited : [];
    const deleted = Array.isArray(data?.deleted) ? data.deleted : [];

    setAttributeFormData((prev) => {
      const nextData = { added, edited, deleted };
      return JSON.stringify(prev) === JSON.stringify(nextData)
        ? prev
        : nextData;
    });
  }, []);

  const attributeFormRows = useMemo(() => {
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const editedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributeIds = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted.map(String)
      : [];

    const editedAttributeMap = new Map(
      editedAttributes.map((attribute) => [String(attribute?.id), attribute]),
    );

    const existingAttributes = (selectedVCItem?.attributes || [])
      .filter((attribute) => {
        const attributeId = attribute?.attribute_id ?? attribute?.id;
        return !deletedAttributeIds.includes(String(attributeId));
      })
      .map((attribute) => {
        const attributeId = attribute?.attribute_id ?? attribute?.id;
        const editedAttribute = editedAttributeMap.get(String(attributeId));

        if (!editedAttribute) {
          return attribute;
        }

        return {
          ...attribute,
          attribute_id: attributeId,
          attribute_type: editedAttribute.attribute_type,
          attribute_sub_type: editedAttribute.attribute_sub_type,
          attribute_heading: editedAttribute.attribute_heading,
          attribute_value: editedAttribute.attribute_value,
          edited: true,
        };
      });

    const addedAttributeRows = addedAttributes.map((attribute) => ({
      ...attribute,
      attribute_id: null,
      newlyAdded: true,
      edited: false,
    }));

    return [...existingAttributes, ...addedAttributeRows];
  }, [
    attributeFormData?.added,
    attributeFormData?.deleted,
    attributeFormData?.edited,
    selectedVCItem?.attributes,
  ]);

  const handleEditVehicleComponent = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const vcId = selectedVCItem?.id;
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    if (!projectId || !variantId || !vcId || !selectedVehicleSystemId) return;

    if (
      addedAttributes.length > 0 ||
      updatedAttributes.length > 0 ||
      deletedAttributes.length > 0
    ) {
      const attributeResp = await dispatch(
        HandleApiActions({
          ...labCar.updateVCAttribute,
          params: {
            entity_id: Number(selectedVCItem?.id),
            new_attributes: addedAttributes,
            updated_attributes: updatedAttributes,
            deleted_attributes: deletedAttributes,
          },
          show_toast: false,
        }),
      );
    }

    const resp = await dispatch(
      HandleApiActions({
        ...labCar.edit_vehicle_component,
        show_toast: true,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          vs_id: Number(selectedVehicleSystemId),
          vc_id: Number(vcId),
          component_number: componentNo?.trim(),
          name: selectedVehicleComponent,
          description,
          suppliers: selectedSuppliers,
          current_time: new Date().toISOString(),
          reason: reason?.trim(),
          ...(addInSettings
            ? {
              add_vc_admin_vs: true,
              add_vc_reason: addInSettingsReason?.trim(),
            }
            : {}),
        },
      }),
    );
    if (!resp?.error) {
      handleCloseDialog();
      getVehicleComponents();
    }
  };

  return (
    <CustomDialogBoxV2
      open={openDialog}
      onClose={handleCloseDialog}
      title={"Edit Vehicle Component"}
      dialogActions={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            {vcEditTabValue === 1 && (
              <CustomButtonV2
                type="cancel"
                size="small"
                btnName="Back"
                handleOnClick={() => {
                  setVcEditTabValue(0);
                }}
              />
            )}
          </Box>

          <CustomButtonV2
            type="submit"
            size="small"
            disabled={
              (vcEditTabValue === 0
                ? !isDialogDetailsValid()
                : !isDialogFieldsValid()) || isLoadingDetails
            }
            btnName={vcEditTabValue === 0 ? "Next" : "Save"}
            handleOnClick={() => {
              if (vcEditTabValue === 0) {
                setVcEditTabValue(1);
              } else {
                handleEditVehicleComponent();
              }
            }}
          />
        </Box>
      }
    >
      <CustomTabsV2
        defaultTab={vcEditTabValue}
        value={vcEditTabValue}
        onTabChange={(newValue) => setVcEditTabValue(newValue)}
        tabs={[
          {
            name: "Details",
            content: (
              <Stack spacing={2} mb={2} sx={{ width: "450px" }}>
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Component No"}
                  required={true}
                  type={"text"}
                  fldType={"textR"}
                  value={componentNo}
                  setValue={(value) =>
                    value.length <= 40 && setComponentNo(value)
                  }
                  maxLength={40}
                />
                <EnterSelectTextField
                  size={"small"}
                  label={"Select / Enter Vehicle Component"}
                  required={true}
                  optionsList={vcOptionsList}
                  value={String(selectedVehicleComponent || "")}
                  setValue={setSelectedVehicleComponent}
                  height={{ height: "32px" }}
                />
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Description"}
                  required={true}
                  value={description}
                  setValue={(value) =>
                    value.length <= 500 && setDescription(value)
                  }
                  maxLength={500}
                />
                <MultiselectConnectorV2
                  required
                  mutiple
                  size={"small"}
                  label={"Select Suppliers"}
                  optionsList={resolvedSupplierOptionsList?.map((item) => ({
                    value: item?.value.toUpperCase(),
                    label: item?.label,
                  }))}
                  selectedValues={selectedSuppliers || []}
                  setSelectedValues={setSelectedSuppliers}
                  placeholder={false}
                />
                <MultiselectConnectorV2
                  mutiple
                  selectedValues={selectedVariants || []}
                  setSelectedValues={setSelectedVariants}
                  label={"Select Variants"}
                  optionsList={vsVariantOptionList}
                  placeholder={false}
                  immutableValues={
                    selectedSingleVariantDetails?.variant_id != null
                      ? [selectedSingleVariantDetails?.variant_id]
                      : []
                  }
                  disabled={true}
                />

                {selectedVehicleComponent && !validateShowAddInSettings ? (
                  <Box display="flex" alignItems="center">
                    <CustomCheckBoxV2
                      checked={addInSettings}
                      disabled={hasPendingVCAddRequest}
                      onChange={() => setAddInSettings((prev) => !prev)}
                      sx={{ pl: 0 }}
                    />
                    <Typography sx={{ cursor: "default" }}>
                      Request to add in settings
                    </Typography>
                  </Box>
                ) : null}
                {addInSettings &&
                  selectedVehicleComponent &&
                  !validateShowAddInSettings ? (
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Reason for settings request"}
                    required={true}
                    value={addInSettingsReason}
                    setValue={(value) =>
                      value.length <= 100 && setAddInSettingsReason(value)
                    }
                    maxLength={100}
                  />
                ) : null}
              </Stack>
            ),
          },
          {
            name: "Attribute",
            content: (
              <Stack
                spacing={2}
                mb={2}
                justifyContent={"center"}
                sx={{ width: "450px" }}
              >
                <AttributesForm
                  type={"EDIT"}
                  attributeFromApi={attributeFormRows}
                  getFormData={handleVCAttributeAdd}
                  reason={reason}
                  setReason={setReason}
                  showReason={true}
                />
              </Stack>
            ),
          },
        ]}
      />
    </CustomDialogBoxV2>
  );
}

export default VehicleComponentsEditDialog;
